'use client';

import { c as _c } from "react/compiler-runtime";
import type { DynamicToolUIPart, ToolUIPart } from 'ai';
import { motion, useReducedMotion } from 'framer-motion';
import { BrainIcon, ChevronRightIcon, LocateFixedIcon, MessageSquarePlusIcon, SearchIcon, SquareTerminalIcon, WrenchIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { isValidElement } from 'react';
import { CodeBlock } from './code-block';
import { AI_DISCLOSURE_CONTENT_CLASS, AI_DISCLOSURE_LABEL_CLASS, AI_DISCLOSURE_TRAILING_ICON_CLASS, AI_DISCLOSURE_TRIGGER_CLASS } from './disclosure-styles';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
export type ToolProps = ComponentProps<typeof Collapsible>;
export const Tool = t0 => {
  const $ = _c(20);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      disableAnimation: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const disableAnimation = t1 === undefined ? false : t1;
  const shouldReduceMotion = useReducedMotion();
  let t2;
  if ($[4] !== className) {
    t2 = cn("group/disclosure not-prose mb-3 box-border min-w-0 w-full max-w-full", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <Collapsible className={t2} {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const collapsible = t3;
  if (disableAnimation) {
    return collapsible;
  }
  let t4;
  if ($[9] !== shouldReduceMotion) {
    t4 = shouldReduceMotion ? undefined : {
      opacity: 0,
      y: -6
    };
    $[9] = shouldReduceMotion;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== shouldReduceMotion) {
    t5 = shouldReduceMotion ? undefined : {
      opacity: 1,
      y: 0
    };
    $[11] = shouldReduceMotion;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== shouldReduceMotion) {
    t6 = shouldReduceMotion ? undefined : {
      duration: 0.18,
      ease: "easeOut"
    };
    $[13] = shouldReduceMotion;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== collapsible || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <motion.div initial={t4} animate={t5} transition={t6}>{collapsible}</motion.div>;
    $[15] = collapsible;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
export type ToolPart = ToolUIPart | DynamicToolUIPart;
export type ToolHeaderProps = {
  title?: ReactNode;
  className?: string;
} & ({
  type: ToolUIPart['type'];
  state: ToolUIPart['state'];
  toolName?: never;
} | {
  type: DynamicToolUIPart['type'];
  state: DynamicToolUIPart['state'];
  toolName: string;
});
const SEARCH_TOOL_NAMES = new Set(['readPrSummary', 'listChangedFiles', 'readChangedFile', 'searchPrArtifacts', 'readRepoMemory', 'listThreads', 'readThread', 'readChecksSummary', 'listCustomAgents', 'get_code_context_exa']);
function getToolHeaderIcon(toolName: string): {
  Icon: LucideIcon;
  kind: string;
} {
  if (toolName === 'bash') {
    return {
      Icon: SquareTerminalIcon,
      kind: 'terminal'
    };
  }
  if (toolName === 'focusCode') {
    return {
      Icon: LocateFixedIcon,
      kind: 'locate'
    };
  }
  if (toolName === 'addPrComment') {
    return {
      Icon: MessageSquarePlusIcon,
      kind: 'comment'
    };
  }
  if (toolName === 'think') {
    return {
      Icon: BrainIcon,
      kind: 'thinking'
    };
  }
  if (SEARCH_TOOL_NAMES.has(toolName)) {
    return {
      Icon: SearchIcon,
      kind: 'search'
    };
  }
  return {
    Icon: WrenchIcon,
    kind: 'tool'
  };
}
export const ToolHeader = t0 => {
  const $ = _c(27);
  let className;
  let props;
  let title;
  let toolName;
  let type;
  if ($[0] !== t0) {
    const {
      className: t1,
      title: t2,
      type: t3,
      state: _state,
      toolName: t4,
      ...t5
    } = t0;
    className = t1;
    title = t2;
    type = t3;
    toolName = t4;
    props = t5;
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = title;
    $[4] = toolName;
    $[5] = type;
  } else {
    className = $[1];
    props = $[2];
    title = $[3];
    toolName = $[4];
    type = $[5];
  }
  let t1;
  if ($[6] !== toolName || $[7] !== type) {
    t1 = type === "dynamic-tool" ? toolName : type.split("-").slice(1).join("-");
    $[6] = toolName;
    $[7] = type;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const derivedName = t1;
  const label = title ?? derivedName;
  let t2;
  if ($[9] !== derivedName) {
    t2 = getToolHeaderIcon(derivedName);
    $[9] = derivedName;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const {
    Icon: HeaderIcon,
    kind: iconKind
  } = t2;
  let t3;
  if ($[11] !== className) {
    t3 = cn(AI_DISCLOSURE_TRIGGER_CLASS, className);
    $[11] = className;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== HeaderIcon || $[14] !== derivedName || $[15] !== iconKind) {
    t4 = <HeaderIcon data-tool-icon-kind={iconKind} data-tool-icon-name={derivedName} className="size-4 text-muted-foreground/70" />;
    $[13] = HeaderIcon;
    $[14] = derivedName;
    $[15] = iconKind;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== label) {
    t5 = <div className={AI_DISCLOSURE_LABEL_CLASS}>{label}</div>;
    $[17] = label;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <div className="flex min-w-0 flex-1 items-center gap-2">{t4}{t5}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronRightIcon className={cn(AI_DISCLOSURE_TRAILING_ICON_CLASS, "group-data-[state=open]/disclosure:rotate-90")} />;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== props || $[24] !== t3 || $[25] !== t6) {
    t8 = <CollapsibleTrigger className={t3} {...props}>{t6}{t7}</CollapsibleTrigger>;
    $[23] = props;
    $[24] = t3;
    $[25] = t6;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;
export const ToolContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(AI_DISCLOSURE_CONTENT_CLASS, "min-w-0 max-w-full space-y-3 overflow-hidden text-popover-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CollapsibleContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type ToolInputProps = ComponentProps<'div'> & {
  input: ToolPart['input'];
};
export const ToolInput = t0 => {
  const $ = _c(15);
  let className;
  let input;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      input,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = input;
    $[3] = props;
  } else {
    className = $[1];
    input = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("min-w-0 max-w-full space-y-2 overflow-hidden", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Parameters</h4>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== input) {
    t3 = JSON.stringify(input, null, 2);
    $[7] = input;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <div className="rounded-md bg-muted/50"><CodeBlock code={t3} language="json" /></div>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t4) {
    t5 = <div className={t1} {...props}>{t2}{t4}</div>;
    $[11] = props;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export type ToolOutputProps = ComponentProps<'div'> & {
  output: ToolPart['output'];
  errorText: ToolPart['errorText'];
};
export const ToolOutput = t0 => {
  const $ = _c(30);
  let className;
  let errorText;
  let output;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      output,
      errorText,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = errorText;
    $[3] = output;
    $[4] = props;
  } else {
    className = $[1];
    errorText = $[2];
    output = $[3];
    props = $[4];
  }
  if (!(output || errorText)) {
    return null;
  }
  const t1 = output as ReactNode;
  let t2;
  if ($[5] !== t1) {
    t2 = <div>{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let Output = t2;
  if (typeof output === "object" && !isValidElement(output)) {
    let t3;
    if ($[7] !== output) {
      t3 = JSON.stringify(output, null, 2);
      $[7] = output;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t3) {
      t4 = <CodeBlock code={t3} language="json" />;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    Output = t4;
  } else {
    if (typeof output === "string") {
      let t3;
      if ($[11] !== output) {
        t3 = <CodeBlock code={output} language="json" />;
        $[11] = output;
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      Output = t3;
    }
  }
  let t3;
  if ($[13] !== className) {
    t3 = cn("min-w-0 max-w-full space-y-2", className);
    $[13] = className;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = errorText ? "Error" : "Result";
  let t5;
  if ($[15] !== t4) {
    t5 = <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{t4}</h4>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = errorText ? "bg-destructive/10 text-destructive" : "bg-muted/50 text-foreground";
  let t7;
  if ($[17] !== t6) {
    t7 = cn("box-border min-w-0 max-w-full overflow-x-auto rounded-md text-xs [&_table]:w-full", t6);
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== errorText) {
    t8 = errorText && <div>{errorText}</div>;
    $[19] = errorText;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== Output || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className={t7}>{t8}{Output}</div>;
    $[21] = Output;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== props || $[26] !== t3 || $[27] !== t5 || $[28] !== t9) {
    t10 = <div className={t3} {...props}>{t5}{t9}</div>;
    $[25] = props;
    $[26] = t3;
    $[27] = t5;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
};